import { FieldType } from 'src/Common/Filters/interfaces/FiltersView';

/**
 * Dashboard filter + selectable-column config for the "Microsoft Marketplace" tenant
 * (recordType `MarketplaceRequest`), modeled on `MEAFilterConfig` / `ROBFilterConfig`. In the Artemis
 * dashboard model these entries are BOTH the filterable fields and the selectable columns (mirroring
 * `filterableAndSelectableColumnsROB`), so `MarketplaceTenantConfig` binds this array to
 * `Hub.FilterConfig` and derives `Hub.SelectableColumns` from its `internalName`s.
 *
 * Filter fields follow tech-spec §2.1 (State, Action Owner, Request Category, Priority, Impacted
 * Geography/Segment, Created/Expected-resolution date) plus the columns called out there (Request
 * Category, Opportunity ID, Referral ID, Known Financial Impact).
 *
 * ⚠️ internalName provenance: the standard RAID paths — `raidid`, `fields/title`,
 * `fields/assignedto/uniquename`, `fields/state`, `fields/createddate` — are VERIFIED against the
 * Marketplace view seed (`marketplace-views.json`). The Marketplace-specific paths (`fields/actioncategory`,
 * `fields/priority`, `fields/impactedgeography`, `fields/impactedsegment`, `fields/opportunityid`,
 * `fields/referralid`, `fields/knownfinancialimpactusd`, `fields/expectedresolutiondate`) are the
 * expected `fields/<engine field name>` convention but are PROVISIONAL until confirmed against how the
 * Marketplace `RAIDEntity` is indexed (submission is still deferred, so nothing is indexed yet — see the
 * `_note` in `marketplace-views.json`). Impacted Region / Impacted Segment are `CustomDropdown`s whose
 * option catalogs are loaded live from Taxonomy Central (CORP_GEO / SEGMENTATION) by
 * `useMarketplaceFilterOptions` and cached in the shared `dropdownData` store under
 * `MARKETPLACE_IMPACTED_GEOGRAPHY_DROPDOWN` / `MARKETPLACE_IMPACTED_SEGMENT_DROPDOWN` — the same single
 * source the create / manage forms use, so filter values never drift from the forms. Request Category
 * (`actionCategory`) and Priority (`priority`) are likewise `CustomDropdown`s hydrated by
 * `useMarketplaceFilterOptions` from that SAME FormEngine field-options endpoint (Request Category
 * resolves the static create-form option set; Priority resolves the RAID-native P0–P6 list), cached
 * under `MARKETPLACE_REQUEST_CATEGORY_DROPDOWN` / `MARKETPLACE_PRIORITY_DROPDOWN`.
 */

/**
 * `dropdownData` store keys under which the Marketplace filter option catalogs are cached, plus the
 * `keyName` (the property holding each option value). Shared with `useMarketplaceFilterOptions` so the
 * filter config and the hydration hook cannot drift.
 */
export const MARKETPLACE_IMPACTED_GEOGRAPHY_DROPDOWN = 'marketplaceImpactedGeography';
export const MARKETPLACE_IMPACTED_SEGMENT_DROPDOWN = 'marketplaceImpactedSegment';
export const MARKETPLACE_REQUEST_CATEGORY_DROPDOWN = 'marketplaceRequestCategory';
export const MARKETPLACE_DATA_SOURCE_DROPDOWN = 'marketplaceDataSource';
export const MARKETPLACE_PRIORITY_DROPDOWN = 'marketplacePriority';
// Marketplace workflow State list. Served by the FormEngine field-options endpoint (backed by the static
// `marketplace.state` option set — the same 14-state vocabulary the Manage form uses), hydrated by
// `useMarketplaceFilterOptions` into this `dropdownData` store key so the State filter shows only the
// Marketplace-applicable states instead of the global all-tenant ADO `workItemStates` list (Bug 144218).
// Like Action Category / Known Scope below, this MUST equal the Marketplace tenant's server-driven Cosmos
// `FieldConfigurations` State `fieldDropdownName` (repointed from the shared `workItemStates` to the
// Marketplace-only `mkT_State`, with `keyName` set to `displayName`) — that server config, not this static
// config, drives the hub filter panel at runtime, so the two must match for the hub to read our 14 states.
export const MARKETPLACE_STATE_DROPDOWN = 'mkT_State';
// These two align to the Marketplace tenant's server-driven Cosmos `FieldConfigurations`
// (`fieldDropdownName` = `mkT_ActionCategory` / `mkT_KnownScopeOfImpact`, `keyName` = `displayName`),
// which is what actually drives the hub filter panel at runtime (the static config here is only the
// empty-FieldConfigurations fallback). The hook stores each option under BOTH `value` and
// `displayName`, so it resolves whether a field's `keyName` is `value` or `displayName`.
export const MARKETPLACE_ACTION_CATEGORY_DROPDOWN = 'mkT_ActionCategory';
export const MARKETPLACE_KNOWN_SCOPE_OF_IMPACT_DROPDOWN = 'mkT_KnownScopeOfImpact';
export const MARKETPLACE_FILTER_OPTION_KEY = 'value';

export const MarketplaceFilterConfig = [
  {
    fieldId: 'ID',
    fieldName: 'ID',
    fieldType: FieldType.TextField,
    internalName: 'raidid',
    responseFormat: 'number',
  },
  {
    fieldId: 'Title',
    fieldName: 'Title',
    fieldType: FieldType.TextField,
    internalName: 'fields/title',
    responseFormat: 'string',
  },
  {
    fieldId: 'State',
    fieldName: 'State',
    fieldType: FieldType.CustomDropdown,
    fieldDropdownName: MARKETPLACE_STATE_DROPDOWN,
    keyName: MARKETPLACE_FILTER_OPTION_KEY,
    internalName: 'fields/state',
    responseFormat: 'string',
  },
  {
    fieldId: 'ActionOwner',
    fieldName: 'Action Owner',
    fieldType: FieldType.PickerComponent,
    internalName: 'fields/assignedto/uniquename',
    responseFormat: 'string',
  },
  {
    fieldId: 'RequestCategory',
    fieldName: 'Request Category',
    fieldType: FieldType.CustomDropdown,
    fieldDropdownName: MARKETPLACE_REQUEST_CATEGORY_DROPDOWN,
    keyName: MARKETPLACE_FILTER_OPTION_KEY,
    internalName: 'fields/actioncategory',
    responseFormat: 'string',
  },
  {
    fieldId: 'Priority',
    fieldName: 'Priority',
    fieldType: FieldType.CustomDropdown,
    fieldDropdownName: MARKETPLACE_PRIORITY_DROPDOWN,
    keyName: MARKETPLACE_FILTER_OPTION_KEY,
    internalName: 'fields/priority',
    responseFormat: 'string',
  },
  {
    fieldId: 'ActionCategory',
    fieldName: 'Action Category',
    fieldType: FieldType.CustomDropdown,
    fieldDropdownName: MARKETPLACE_ACTION_CATEGORY_DROPDOWN,
    keyName: MARKETPLACE_FILTER_OPTION_KEY,
    internalName: 'fields/mktactioncategory',
    responseFormat: 'string',
  },
  {
    fieldId: 'KnownScopeOfImpact',
    fieldName: 'Known Scope of Impact',
    fieldType: FieldType.CustomDropdown,
    fieldDropdownName: MARKETPLACE_KNOWN_SCOPE_OF_IMPACT_DROPDOWN,
    keyName: MARKETPLACE_FILTER_OPTION_KEY,
    internalName: 'fields/knownscopeofimpact',
    responseFormat: 'string',
  },
  {
    fieldId: 'ImpactedGeography',
    fieldName: 'Impacted Geography',
    fieldType: FieldType.CustomDropdown,
    fieldDropdownName: MARKETPLACE_IMPACTED_GEOGRAPHY_DROPDOWN,
    keyName: MARKETPLACE_FILTER_OPTION_KEY,
    internalName: 'fields/impactedgeography',
    responseFormat: 'string',
  },
  {
    fieldId: 'ImpactedSegment',
    fieldName: 'Impacted Segment',
    fieldType: FieldType.CustomDropdown,
    fieldDropdownName: MARKETPLACE_IMPACTED_SEGMENT_DROPDOWN,
    keyName: MARKETPLACE_FILTER_OPTION_KEY,
    internalName: 'fields/impactedsegment',
    responseFormat: 'string',
  },
  {
    fieldId: 'OpportunityId',
    fieldName: 'Opportunity ID',
    fieldType: FieldType.TextField,
    internalName: 'fields/opportunityid',
    responseFormat: 'string',
  },
  {
    fieldId: 'ReferralId',
    fieldName: 'Referral ID',
    fieldType: FieldType.TextField,
    internalName: 'fields/referralid',
    responseFormat: 'string',
  },
  {
    fieldId: 'KnownFinancialImpact',
    fieldName: 'Known Financial Impact (USD)',
    fieldType: FieldType.TextField,
    internalName: 'fields/knownfinancialimpactusd',
    responseFormat: 'number',
  },
  {
    fieldId: 'CreatedDate',
    fieldName: 'Created Date',
    fieldType: FieldType.DatePicker,
    internalName: 'fields/createddate',
    responseFormat: 'date',
    isMaxCurrentDate: true,
  },
  {
    fieldId: 'ExpectedResolutionDate',
    fieldName: 'Expected Resolution Date',
    fieldType: FieldType.DatePicker,
    internalName: 'fields/expectedresolutiondate',
    responseFormat: 'date',
  },
];
// Generated by GitHub Copilot | Created on: 2026-07-14 | New file | Marketplace dashboard filter/column config (§2.1 filters + Request Category/Opportunity ID/Referral ID/Known Financial Impact columns); standard RAID paths verified, Marketplace-specific paths provisional
// Generated by GitHub Copilot | Updated on: 2026-07-28 | 24 lines added | Added Action Category (mktActionCategory) + Known Scope of Impact (knownScopeOfImpact) CustomDropdown columns + store-key constants (options-API hydrated); index paths provisional
// Generated by GitHub Copilot | Updated on: 2026-07-28 | 2 lines modified | Repoint Action Category / Known Scope store keys to Cosmos fieldDropdownName (mkT_ActionCategory / mkT_KnownScopeOfImpact) so the hub filter dropdowns resolve
// Generated by GitHub Copilot | Updated on: 2026-08-10 | 8 lines modified | State filter repointed from global workItemStates Dropdown to a Marketplace-scoped CustomDropdown (MARKETPLACE_STATE_DROPDOWN, options-API hydrated) so only Marketplace states show (Bug 144218)
// Generated by GitHub Copilot | Updated on: 2026-08-14 | 1 line added | Added Data Source dropdown store key
